import React from 'react';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Box, Chip } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import RemoveCircleIcon from '@material-ui/icons/RemoveCircle';
import MailIcon from '@material-ui/icons/Mail';
import {
  RegistrationRequestStatus,
  RegistrationRequestStatusEnum,
} from '../types';
import {
  ApproveButton,
  DeclineButton,
  getStatusColor,
} from '../UserRequestsStyles';
import { useTheme } from '@material-ui/core/styles';
import { formatUtc } from '../../../shared/utils';
import { Palette } from '@material-ui/core/styles/createPalette';

function getStatusIcon(
  status: RegistrationRequestStatus,
  palette: Palette,
): JSX.Element {
  switch (status) {
    case RegistrationRequestStatusEnum.APPROVED:
      return (
        <CheckCircleIcon
          style={{ color: getStatusColor('approved', palette), fontSize: 20 }}
        />
      );
    case RegistrationRequestStatusEnum.DECLINED:
      return (
        <RemoveCircleIcon
          style={{ color: getStatusColor('declined', palette), fontSize: 20 }}
        />
      );
    case RegistrationRequestStatusEnum.NEW:
      return (
        <MailIcon
          style={{ color: getStatusColor('new', palette), fontSize: 20 }}
        />
      );
    default:
      return (
        <CheckCircleIcon
          style={{ color: getStatusColor('approved', palette), fontSize: 20 }}
        />
      );
  }
}

export const buildRegistrationColumns = (
  onApprove: (id: string) => void,
  onDecline: (id: string) => void,
  canWrite = true,
  formatDate: (dateIso?: string | null) => string = formatUtc,
): GridColDef[] => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { palette } = useTheme();
  const approveColor = palette.primary.main;
  const declineColor = palette.error.main;
  const columns: GridColDef[] = [
    {
      flex: 1,
      field: 'requestId',
      headerName: 'Request ID',
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'createdDate',
      headerName: 'Create Date',
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatDate(params?.row?.createdDate)}</span>
      ),
    },
    {
      flex: 1,
      field: 'lastModifiedDate',
      headerName: 'Update Date',
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <span>{formatDate(params?.row?.lastModifiedDate)}</span>
      ),
    },
    {
      flex: 1,
      field: 'email',
      headerName: 'Email',
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'companyName',
      headerName: 'Company Name',
      disableColumnMenu: true,
      minWidth: 120,
      renderCell: (params: GridRenderCellParams) => (
        <Chip label={params.row.companyName ?? ''} color="primary" />
      ),
    },
    {
      flex: 1,
      field: 'name',
      headerName: 'Name',
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'phoneNumber',
      headerName: 'Phone Number',
      disableColumnMenu: true,
    },
    {
      flex: 1,
      field: 'status',
      headerName: 'Status',
      disableColumnMenu: true,
      renderCell: (params: GridRenderCellParams) => (
        <Box style={{ display: 'flex', alignItems: 'center' }}>
          {getStatusIcon(params.value, palette)}
          <span style={{ marginLeft: 8, textTransform: 'capitalize' }}>
            {params.value.replace('_', ' ')}
          </span>
        </Box>
      ),
    },
  ];

  if (canWrite) {
    columns.push({
      flex: 2,
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      filterable: false,
      minWidth: 300,
      cellClassName: 'actions-cell',
      renderCell: (params: GridRenderCellParams) => {
        const row = params.row;
        if (!params) return null;
        if (row.status !== RegistrationRequestStatusEnum.NEW) return null;

        return (
          <Box style={{ display: 'inline-flex', alignItems: 'center' }}>
            <ApproveButton
              size="small"
              variant="outlined"
              onClick={() => onApprove(row.id)}
              startIcon={
                <CheckCircleIcon
                  style={{ color: approveColor, fontSize: 20 }}
                />
              }
            >
              Approve
            </ApproveButton>
            <DeclineButton
              size="small"
              variant="outlined"
              onClick={() => onDecline(row.id)}
              startIcon={
                <RemoveCircleIcon
                  style={{ color: declineColor, fontSize: 20 }}
                />
              }
            >
              Decline
            </DeclineButton>
          </Box>
        );
      },
    });
  }

  return columns;
};
